import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Brain, AlertCircle, CheckCircle, XCircle, ArrowRight, RotateCcw } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { getQuizQuestions, getModules, saveQuizResult } from '../mockApi';
import { api } from '../services/api';
import type { QuizQuestion, ProgressiveQuizQuestion, Module, QuizResult, QuizSubmission } from '../types';

export default function QuizSession() {
  const { theme } = useTheme();
  const { font } = useFont();
  const { updateUserState } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const moduleIds = location.state?.moduleIds || [];
  const difficulty = location.state?.difficulty || 'moyen';
  const mode = location.state?.mode || 'progressive';

  const [questions, setQuestions] = useState<(QuizQuestion | ProgressiveQuizQuestion)[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [selectedAnswerLetter, setSelectedAnswerLetter] = useState<string | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [results, setResults] = useState<QuizResult | null>(null);
  const [answers, setAnswers] = useState<number[]>([]);
  const [progressiveAnswers, setProgressiveAnswers] = useState<{question_id: string, selected: string}[]>([]);

  useEffect(() => {
    if (moduleIds.length === 0) {
      navigate('/qcm');
      return;
    }

    if (mode === 'progressive') {
      Promise.all([
        api.getProgressiveQuizQuestions(),
        getModules()
      ]).then(([progressiveQuestions, fetchedModules]) => {
        setQuestions(progressiveQuestions);
        setModules(fetchedModules);
      });
    } else if (mode === 'free') {
      // Convert difficulty to API format
      const apiDifficulty = difficulty === 'facile' ? 'facile' :
                           difficulty === 'moyen' ? 'mix' :
                           difficulty === 'difficile' ? 'difficile' : 'mix';

      // Convert module IDs to module codes, excluding VAE modules
      Promise.all([
        getModules(),
        api.getFreeQuizQuestions(apiDifficulty, moduleIds.filter(id => !id.startsWith('VAE-')))
      ]).then(([fetchedModules, freeQuestions]) => {
        setQuestions(freeQuestions);
        setModules(fetchedModules);
      });
    } else {
      // Fallback to old system for compatibility
      Promise.all([
        getQuizQuestions(moduleIds),
        getModules()
      ]).then(([allQuestions, fetchedModules]) => {
        // Filter questions by selected difficulty
        const filteredQuestions = allQuestions.filter(q => q.difficulty === difficulty);
        setQuestions(filteredQuestions);
        setModules(fetchedModules);
      });
    }
  }, [moduleIds, difficulty, navigate]);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  const isProgressiveQuestion = (question: QuizQuestion | ProgressiveQuizQuestion): question is ProgressiveQuizQuestion => {
    return '_id' in question && 'choices' in question;
  };

  const getModuleCode = (moduleId: string): string => {
    const module = modules.find(m => m.id === moduleId);
    return module?.code || '';
  };

  const getQuestionOptions = (question: QuizQuestion | ProgressiveQuizQuestion): string[] => {
    if (isProgressiveQuestion(question)) {
      // Filter out empty choices
      const choices = [question.choices.A, question.choices.B, question.choices.C, question.choices.D];
      return choices.filter(choice => choice && choice.trim() !== '');
    }
    return question.options;
  };

  const getValidChoiceLetters = (question: ProgressiveQuizQuestion): string[] => {
    const letters = ['A', 'B', 'C', 'D'];
    const choices = [question.choices.A, question.choices.B, question.choices.C, question.choices.D];
    return letters.filter((letter, index) => choices[index] && choices[index].trim() !== '');
  };

  const getCorrectAnswerIndex = (question: QuizQuestion | ProgressiveQuizQuestion): number => {
    if (isProgressiveQuestion(question)) {
      const validLetters = getValidChoiceLetters(question);
      return validLetters.indexOf(question.correct_answer);
    }
    return question.correctAnswer;
  };

  const getDifficultyLabel = (difficulty: string): string => {
    switch (difficulty) {
      case 'facile':
        return 'Facile';
      case 'moyen':
        return 'Moyen';
      case 'mix':
        return 'Mix';
      case 'difficile':
        return 'Difficile';
      default:
        return difficulty;
    }
  };

  const submitAnswer = () => {
    if (mode === 'progressive' || mode === 'free') {
      if (selectedAnswerLetter === null) return;

      const newAnswers = [...progressiveAnswers];
      newAnswers[currentQuestionIndex] = {
        question_id: (currentQuestion as ProgressiveQuizQuestion)._id,
        selected: selectedAnswerLetter
      };
      setProgressiveAnswers(newAnswers);
    } else {
      if (selectedAnswer === null) return;

      const newAnswers = [...answers];
      newAnswers[currentQuestionIndex] = selectedAnswer;
      setAnswers(newAnswers);
    }

    setHasAnswered(true);
  };

  const showResults = async () => {
    if (mode === 'progressive' || mode === 'free') {
      try {
        // For progressive mode: always send all 5 BC modules
        // For free mode: get module codes from the questions actually answered
        let moduleCodes: string[];

        if (mode === 'progressive') {
          // Always send all 5 BC modules in progressive mode
          moduleCodes = ['BC1', 'BC2', 'BC3', 'BC4', 'BC5'];
        } else {
          // For free mode, get module codes from the questions actually answered
          moduleCodes = Array.from(new Set(
            progressiveAnswers.map((answer, index) => {
              const question = questions[index] as ProgressiveQuizQuestion;
              return question.module_code;
            }).filter(code => code.startsWith('BC'))
          ));
        }

        const submission: QuizSubmission = {
          mode: mode as 'progressive' | 'free',
          difficulty: mode === 'progressive' && difficulty === 'moyen' ? 'medium' :
                     difficulty === 'facile' ? 'easy' :
                     difficulty === 'moyen' ? 'mix' : 'difficult',
          answers: progressiveAnswers,
          module_codes: moduleCodes
        };

        const result = await api.submitQuizResult(submission);

        // Convert to legacy format for display
        const legacyResult = {
          totalQuestions: result.total,
          correctAnswers: result.correct,
          moduleResults: [], // We'll need to group by modules if needed
          date: new Date().toISOString()
        };

        setResults(legacyResult);

        // Update user state to refresh unlocked modes
        await updateUserState();
      } catch (error) {
        console.error('Error submitting quiz:', error);
        alert('Erreur lors de la soumission du quiz');
      }
    } else {
      // Original logic for free mode
      const moduleResults = moduleIds.map(moduleId => {
        const moduleQuestions = questions.filter(q =>
          isProgressiveQuestion(q) ? q.module_code === moduleId : q.moduleId === moduleId
        );
        const correct = moduleQuestions.reduce((acc, q, idx) => {
          return acc + (answers[idx] === getCorrectAnswerIndex(q) ? 1 : 0);
        }, 0);
        return {
          moduleId,
          correct,
          total: moduleQuestions.length
        };
      });

      const totalCorrect = moduleResults.reduce((acc, r) => acc + r.correct, 0);
      const result = {
        totalQuestions: questions.length,
        correctAnswers: totalCorrect,
        moduleResults,
        date: new Date().toISOString()
      };

      // Save the result
      saveQuizResult(result).then(() => {
        setResults(result);
      });
    }
  };

  const nextQuestion = () => {
    setSelectedAnswer(null);
    setSelectedAnswerLetter(null);
    setHasAnswered(false);
    setCurrentQuestionIndex(prev => prev + 1);
  };

  const handleAnswerSelect = (index: number) => {
    if (hasAnswered) return;

    if (mode === 'progressive' || mode === 'free') {
      const validLetters = getValidChoiceLetters(currentQuestion as ProgressiveQuizQuestion);
      setSelectedAnswerLetter(validLetters[index]);
    } else {
      setSelectedAnswer(index);
    }
  };

  const restartQuiz = () => {
    // Check if user has active subscription
    if (hasActiveSubscription()) {
      navigate('/qcm');
    } else {
      // For free users, redirect to dashboard after quiz completion
      navigate('/dashboard');
    }
  };

  if (results) {
    const percentage = Math.round((results.correctAnswers / results.totalQuestions) * 100);
    const getModuleName = (moduleId: string) =>
      modules.find(m => m.id === moduleId)?.title || 'Module inconnu';

    return (
      <div className="min-h-screen bg-gray-50 py-12" style={{ fontFamily: font.family }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="text-center mb-8">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-${theme.secondary} mb-4`}>
                <Brain className={`h-8 w-8 text-${theme.text}`} />
              </div>
              <h1 className="text-2xl font-bold mb-2">Résultats de votre évaluation</h1>
              <div className={`text-4xl font-bold ${percentage >= 70 ? `text-${theme.primary}` : 'text-gray-600'}`}>
                {percentage}%
              </div>
              <p className="text-gray-600 mt-2">
                {results.correctAnswers} réponses correctes sur {results.totalQuestions} questions
              </p>
              {(mode === 'progressive' || mode === 'free') && percentage === 100 && (
                <div className={`mt-4 p-4 rounded-lg bg-${theme.secondary} border border-${theme.primary}`}>
                  <p className={`text-${theme.text} font-medium`}>
                    🎉 Félicitations ! Vous avez débloqué de nouveaux contenus !
                  </p>
                </div>
              )}
            </div>

            {results.moduleResults && results.moduleResults.length > 0 && (
              <div className="space-y-4 mb-8">
              {results.moduleResults.map((moduleResult) => (
                <div key={moduleResult.moduleId} className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium mb-2">{getModuleName(moduleResult.moduleId)}</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className={`h-full bg-${theme.primary} rounded-full`}
                          style={{ width: `${(moduleResult.correct / moduleResult.total) * 100}%` }}
                        />
                      </div>
                    </div>
                    <span className="ml-4 text-sm font-medium">
                      {moduleResult.correct}/{moduleResult.total}
                    </span>
                  </div>
                </div>
              ))}
              </div>
            )}

            <button
              onClick={restartQuiz}
              className={`w-full flex items-center justify-center px-6 py-3 rounded-lg bg-${theme.primary} text-white hover:opacity-90 transition-opacity`}
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Recommencer un quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion) return null;

  const questionOptions = getQuestionOptions(currentQuestion);
  const correctAnswerIndex = getCorrectAnswerIndex(currentQuestion);
  const moduleCode = isProgressiveQuestion(currentQuestion)
    ? currentQuestion.module_code
    : getModuleCode(currentQuestion.moduleId);
  return (
    <div className="min-h-screen bg-gray-50 py-12" style={{ fontFamily: font.family }}>
      <div className="max-w-2xl mx-auto px-4">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">
              Question {currentQuestionIndex + 1}/{questions.length}
            </div>
            <div className="text-sm font-medium text-gray-600">
              {Math.round(progress)}%
            </div>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full">
            <div
              className={`h-full bg-${theme.primary} rounded-full transition-all duration-300`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className={`px-3 py-1 rounded-full bg-${theme.secondary} text-sm font-medium text-${theme.text}`}>
            {moduleCode}
          </div>
          <div className={`px-3 py-1 rounded-full bg-${theme.secondary} text-sm font-medium text-${theme.text}`}>
            {getDifficultyLabel(currentQuestion.difficulty)}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <h2 className="text-xl font-semibold mb-6">{currentQuestion.question}</h2>

          <div className="space-y-3">
            {questionOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                disabled={hasAnswered}
                className={`
                  w-full text-left p-4 rounded-lg border-2 transition-all duration-200
                  ${hasAnswered
                    ? index === correctAnswerIndex
                      ? 'border-green-500 bg-green-50'
                      : index === ((mode === 'progressive' || mode === 'free') ? getValidChoiceLetters(currentQuestion as ProgressiveQuizQuestion).indexOf(selectedAnswerLetter || '') : selectedAnswer)
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200'
                    : ((mode === 'progressive' || mode === 'free') ? selectedAnswerLetter === getValidChoiceLetters(currentQuestion as ProgressiveQuizQuestion)[index] : selectedAnswer === index)
                      ? `border-${theme.primary} bg-${theme.secondary}`
                      : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                <div className="flex items-center">
                  <div className="flex-1">{option}</div>
                  {hasAnswered && (
                    <div className="ml-3">
                      {index === correctAnswerIndex ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : index === ((mode === 'progressive' || mode === 'free') ? getValidChoiceLetters(currentQuestion as ProgressiveQuizQuestion).indexOf(selectedAnswerLetter || '') : selectedAnswer) ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : null}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {hasAnswered && (
            <div className={`mt-6 p-4 rounded-lg bg-${theme.secondary} border border-${theme.primary}`}>
              <div className="flex items-start">
                <Brain className={`h-5 w-5 mt-0.5 mr-3 text-${theme.text}`} />
                <div>
                  <h3 className="font-semibold mb-2">Explication</h3>
                  <p className="text-gray-600">{currentQuestion.explanation}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-end">
            {!hasAnswered ? (
              <button
                onClick={submitAnswer}
                disabled={(mode === 'progressive' || mode === 'free') ? selectedAnswerLetter === null : selectedAnswer === null}
                className={`
                  flex items-center px-6 py-3 rounded-lg font-medium
                  ${((mode === 'progressive' || mode === 'free') ? selectedAnswerLetter !== null : selectedAnswer !== null)
                    ? `bg-${theme.primary} text-white hover:opacity-90`
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                Valider
                <AlertCircle className="ml-2 w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={currentQuestionIndex === questions.length - 1 ? showResults : nextQuestion}
                className={`flex items-center px-6 py-3 rounded-lg bg-${theme.primary} text-white hover:opacity-90`}
              >
                {currentQuestionIndex === questions.length - 1 ? 'Voir mon score' : 'Question suivante'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}